import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ContextMenu } from "primereact/contextmenu";
import { Menu } from "primereact/menu";
import { MenuItem } from "primereact/menuitem";
import AppDialog from "../../Components/Dialogs/AppDialog/AppDialog";
import { usePageHeader } from "../../Components/Template/PageTitle/PageContext";
import LinkedKnowledgeArticlesInfoButton from "../../Components/Custom/KnowledgeBase/LinkedKnowledgeArticlesInfoButton";
import PageBackLink from "../../Components/Custom/PageBackLink";
import PermissionCheck from "../../Components/PermissionCheck";
import DashlyCommentSection from "../../Components/DashlyComment/DashlyCommentSection";
import DetailedActivityStream, {
    IDetailedActivityEvent,
    resolveDetailedActivityEventLabel,
} from "../../Components/DetailedActivityStream/DetailedActivityStream";
import ReviewWidget from "../../Components/Custom/Review/ReviewWidget";
import { IReviewSummary, ReviewService } from "../../Services/ReviewService";
import { getStoredUser } from "../../Util/secureStorageSession";
import { resolveApiErrorMessage } from "../../Util/apiErrorUtil";
import { formatDisplayDate } from "../../Util/dateUtil";
import { RESOURCE_LOCATION } from "../../Constants/resourceLocations";
import {
    IKnowledgeArticleEvent,
    IKnowledgeArticleModel,
    KnowledgeArticleService,
    toKnowledgeArticleCrudModel,
} from "../../Services/KnowledgeArticleService";
import { IKnowledgeArticleSectionModel, SECTION_TYPE } from "../../Services/KnowledgeArticleSectionService";
import {
    IMediaAssetModel,
    MEDIA_TYPE,
    MediaAssetService,
    VIDEO_RESOLUTION_OPTIONS,
    mediaThumbnailUrl,
    photoDisplayUrl,
} from "../../Services/MediaAssetService";
import MediaManager, { MediaManagerHandle } from "./MediaManager";
import ArticleSectionViewer from "./ArticleSectionViewer";
import VideoPlayer, { VideoPlayerSource } from "./VideoPlayer";
import { resolveVideoPlayerSources } from "./resolveVideoPlayerSources";
import {
    KNOWLEDGE_ARTICLE_SECTION_QUERY,
    KNOWLEDGE_SECTION_ANCHOR_BLINK_COUNT,
    KNOWLEDGE_SECTION_ANCHOR_BLINK_DELAY_MS,
    KNOWLEDGE_SECTION_ANCHOR_BLINK_OFF_MS,
    KNOWLEDGE_SECTION_ANCHOR_BLINK_ON_MS,
    KNOWLEDGE_ARTICLE_STATUS,
    KNOWLEDGE_BASE_LIST_PATH,
    estimateReadingTimeMinutes,
    knowledgeArticleSectionDomId,
    isFeaturedActive,
    isFeaturedExpired,
    knowledgeArticleEditPath,
    resolveKnowledgeArticleIconClass,
} from "./knowledgeBaseConstants";
import { exportKnowledgeArticleToPdf } from "./knowledgeArticlePdfExport";
import { exportKnowledgeArticleFullArchive } from "./knowledgeArticleFullExport";
import KnowledgeBaseAskAi from "./KnowledgeBaseAskAi";
import { useMediaUrlRefresh } from "./useMediaUrlRefresh";
import "./KnowledgeArticleDetail.scss";

const KNOWLEDGE_ARTICLE_EVENT_PREFIX = "article_";

const KnowledgeArticleDetailPage: React.FC = () => {
    const { uuid } = useParams<{ uuid: string }>();
    const [searchParams] = useSearchParams();
    const sectionParam = searchParams.get(KNOWLEDGE_ARTICLE_SECTION_QUERY);
    const [highlightedSectionUuid, setHighlightedSectionUuid] = useState<string | null>(null);
    const sectionAnchorPulseTimersRef = useRef<number[]>([]);
    const sectionAnchorScheduledForRef = useRef<string | null>(null);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { setPageHeader, resetPageHeader } = usePageHeader();

    const knowledgeArticleService = useMemo(() => new KnowledgeArticleService(), []);
    const mediaAssetService = useMemo(() => new MediaAssetService(), []);
    const reviewService = useMemo(() => new ReviewService(), []);
    const currentUserUuid = useMemo(() => getStoredUser()?.uuid, []);
    const canComment = useMemo(() => PermissionCheck("KnowledgeBase.Comment"), []);
    const hasManagePermission = useMemo(() => PermissionCheck("KnowledgeBase.Manage"), []);

    const [article, setArticle] = useState<IKnowledgeArticleModel | null>(null);
    const [loadFailed, setLoadFailed] = useState(false);
    // An article shared down from a customer above is read-only for everyone, KnowledgeBase.Manage included.
    const canManage = hasManagePermission && !!article && article.manageable !== false;
    const categories = useMemo(() => article?.categories ?? [], [article]);
    const [pinned, setPinned] = useState(false);
    const [events, setEvents] = useState<IKnowledgeArticleEvent[]>([]);
    const [activityOpen, setActivityOpen] = useState(false);
    const [activityLoaded, setActivityLoaded] = useState(false);
    const [activityStale, setActivityStale] = useState(false);
    const [activityLoading, setActivityLoading] = useState(false);
    const [commentCount, setCommentCount] = useState(0);
    const [sections, setSections] = useState<IKnowledgeArticleSectionModel[]>([]);
    const [mediaAssets, setMediaAssets] = useState<IMediaAssetModel[]>([]);
    // Only the first load of the list: a background refresh of the signed URLs must not put the skeletons
    // back over media that is already on screen.
    const [mediaLoading, setMediaLoading] = useState(true);
    const [contextAsset, setContextAsset] = useState<IMediaAssetModel | null>(null);
    const [enlargedAsset, setEnlargedAsset] = useState<{ asset: IMediaAssetModel; videoSources?: VideoPlayerSource[] } | null>(null);
    const [reviewSummary, setReviewSummary] = useState<IReviewSummary | null>(null);
    const [reviewSummaryLoaded, setReviewSummaryLoaded] = useState(false);
    // Once the visitor has answered "was this helpful", the question is tucked away and the thumb in the
    // statistics shows their answer; clicking that thumb takes the answer back, which brings the question back.
    // Expanded keeps the card up while a thumbs-down is still asking for feedback.
    const [reviewExpanded, setReviewExpanded] = useState(false);
    const [withdrawingReview, setWithdrawingReview] = useState(false);
    const [exportingPdf, setExportingPdf] = useState(false);
    const [exportingFull, setExportingFull] = useState(false);
    const [statusSaving, setStatusSaving] = useState(false);
    const attachmentContextMenuRef = useRef<ContextMenu>(null);
    const articleHeaderMenuRef = useRef<Menu>(null);
    const mediaManagerRef = useRef<MediaManagerHandle>(null);

    // The article comes with its sections, its categories and the pin state — one request, not four.
    const load = useCallback(async () => {
        if (!uuid) {
            return;
        }
        try {
            const response = await knowledgeArticleService.fetchItem(uuid);
            const loaded = response.data.data;
            setArticle(loaded);
            setSections(loaded.sections ?? []);
            setPinned(loaded.pinned === true);
        } catch {
            setLoadFailed(true);
        }
    }, [uuid, knowledgeArticleService]);

    // Management-only, and only fetched once the timeline is opened (or a full export needs it).
    const loadEvents = useCallback(async (): Promise<IKnowledgeArticleEvent[]> => {
        if (!uuid || !canManage) {
            return [];
        }
        setActivityLoading(true);
        try {
            const response = await knowledgeArticleService.fetchEvents(uuid);
            const next = response.data.data ?? [];
            setEvents(next);
            setActivityLoaded(true);
            setActivityStale(false);
            return next;
        } catch {
            // The timeline is an extra: the article itself stays usable without it.
            return [];
        } finally {
            setActivityLoading(false);
        }
    }, [uuid, knowledgeArticleService, canManage]);

    const refreshActivityIfNeeded = useCallback(() => {
        if (activityOpen) {
            void loadEvents();
            return;
        }
        if (activityLoaded) {
            setActivityStale(true);
        }
    }, [activityOpen, activityLoaded, loadEvents]);

    const toggleActivity = () => {
        const next = !activityOpen;
        setActivityOpen(next);
        if (next && (!activityLoaded || activityStale)) {
            void loadEvents();
        }
    };

    const loadReviewSummary = useCallback(async () => {
        if (!uuid) {
            return;
        }
        try {
            const response = await reviewService.fetchSummary(RESOURCE_LOCATION.KNOWLEDGE_ARTICLE, uuid);
            setReviewSummary(response.data.data);
        } catch {
            // Same: no rating statistics is no reason to fail the page.
        } finally {
            setReviewSummaryLoaded(true);
        }
    }, [uuid, reviewService]);

    useEffect(() => {
        setReviewExpanded(false);
    }, [uuid]);

    const withdrawOwnReview = async () => {
        if (!uuid || withdrawingReview) {
            return;
        }
        setWithdrawingReview(true);
        try {
            await reviewService.withdrawReview(RESOURCE_LOCATION.KNOWLEDGE_ARTICLE, uuid);
            setReviewExpanded(false);
            await loadReviewSummary();
        } catch (error) {
            toast.error(resolveApiErrorMessage(error, "review.saveError", t));
        } finally {
            setWithdrawingReview(false);
        }
    };

    const sectionIdsKey = sections.map((section) => section.uuid).join(",");

    useEffect(() => {
        return () => {
            sectionAnchorPulseTimersRef.current.forEach((timer) => window.clearTimeout(timer));
            sectionAnchorPulseTimersRef.current = [];
        };
    }, []);

    useEffect(() => {
        if (!sectionParam || !sectionIdsKey) {
            return;
        }
        const el = document.getElementById(knowledgeArticleSectionDomId(sectionParam));
        if (!el) {
            return;
        }
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        if (sectionAnchorScheduledForRef.current === sectionParam) {
            return;
        }
        sectionAnchorPulseTimersRef.current.forEach((timer) => window.clearTimeout(timer));
        sectionAnchorPulseTimersRef.current = [];
        setHighlightedSectionUuid(null);
        const startBlink = window.setTimeout(() => {
            sectionAnchorScheduledForRef.current = sectionParam;
            let offsetMs = 0;
            for (let pulse = 0; pulse < KNOWLEDGE_SECTION_ANCHOR_BLINK_COUNT; pulse += 1) {
                const onTimer = window.setTimeout(() => setHighlightedSectionUuid(sectionParam), offsetMs);
                offsetMs += KNOWLEDGE_SECTION_ANCHOR_BLINK_ON_MS;
                const offTimer = window.setTimeout(() => setHighlightedSectionUuid(null), offsetMs);
                offsetMs += KNOWLEDGE_SECTION_ANCHOR_BLINK_OFF_MS;
                sectionAnchorPulseTimersRef.current.push(onTimer, offTimer);
            }
        }, KNOWLEDGE_SECTION_ANCHOR_BLINK_DELAY_MS);
        return () => window.clearTimeout(startBlink);
    }, [sectionParam, sectionIdsKey]);

    // Every asset carries the signed URLs the page shows it with — one request for all the media.
    const loadMedia = useCallback(async () => {
        if (!uuid) {
            return;
        }
        try {
            const response = await mediaAssetService.fetchByArticle(uuid);
            setMediaAssets(response.data.data ?? []);
        } catch (error) {
            toast.error(resolveApiErrorMessage(error, "knowledgeBase.form.mediaLoadError", t));
        } finally {
            setMediaLoading(false);
        }
    }, [uuid, mediaAssetService, t]);

    useMediaUrlRefresh(loadMedia);

    const handleFileDownload = async (assetUuid: string, variantType?: string) => {
        const loadingToastId = toast.loading(t("common.loading") as string);
        try {
            const response = await mediaAssetService.fetchDownloadUrl(assetUuid, variantType);
            window.open(response.data.data, "_blank", "noopener,noreferrer");
            toast.dismiss(loadingToastId);
        } catch (error) {
            toast.update(loadingToastId, {
                type: "error",
                render: variantType
                    ? (t("knowledgeBase.form.mediaResolutionNotReady") as string)
                    : resolveApiErrorMessage(error, "knowledgeBase.form.mediaDownloadError", t),
                isLoading: false,
                autoClose: 3000,
            });
        }
    };

    // Photos and videos open enlarged — their URLs are already in the list. A plain file is only downloaded.
    const isEnlargeableVideo = (asset: IMediaAssetModel) => asset.mediaType === MEDIA_TYPE.VIDEO;
    const isEnlargeable = (asset: IMediaAssetModel) => asset.mediaType === MEDIA_TYPE.PHOTO || isEnlargeableVideo(asset);

    const openEnlarged = (asset: IMediaAssetModel) => {
        if (!isEnlargeableVideo(asset)) {
            setEnlargedAsset({ asset });
            return;
        }
        const videoSources = resolveVideoPlayerSources(asset);
        if (videoSources.length === 0) {
            toast.error(t("knowledgeBase.form.mediaResolutionNotReady") as string);
            return;
        }
        setEnlargedAsset({ asset, videoSources });
    };

    const handleDeleteAttachment = async (assetUuid: string) => {
        const loadingToastId = toast.loading(t("common.saving") as string);
        try {
            await mediaAssetService.deleteMediaAsset(assetUuid);
            // The backend takes a deleted inline photo out of the text it was in: reload the article too.
            await Promise.all([load(), loadMedia()]);
            refreshActivityIfNeeded();
            toast.update(loadingToastId, { type: "success", render: t("common.saved") as string, isLoading: false, autoClose: 1500 });
        } catch (error) {
            toast.update(loadingToastId, {
                type: "error",
                render: resolveApiErrorMessage(error, "knowledgeBase.form.mediaDeleteError", t),
                isLoading: false,
                autoClose: 3000,
            });
        }
    };

    // The Attachments list now also surfaces section media (IMAGE/VIDEO/SLIDESHOW items) alongside plain
    // FILE uploads, so a video gets the resolution-picker submenu and anything enlargeable gets the
    // "show enlarged" entry, same as before the section rewrite.
    const attachmentMenuItems: MenuItem[] = useMemo(() => {
        if (!contextAsset) {
            return [];
        }
        const items: MenuItem[] = isEnlargeableVideo(contextAsset)
            ? [{
                label: t("common.action.download") as string,
                icon: "pi pi-download",
                items: [
                    { label: t("knowledgeBase.form.resolutionOriginal") as string, command: () => handleFileDownload(contextAsset.uuid) },
                    // Only the renditions this video actually has.
                    ...VIDEO_RESOLUTION_OPTIONS.filter((option) =>
                        contextAsset.renditions?.some((rendition) => rendition.variantType === option.variantType)
                    ).map((option) => ({
                        label: option.label,
                        command: () => handleFileDownload(contextAsset.uuid, option.variantType),
                    })),
                ],
            }]
            : [
                { label: t("common.action.download") as string, icon: "pi pi-download", command: () => handleFileDownload(contextAsset.uuid) },
            ];
        if (isEnlargeable(contextAsset)) {
            items.push({
                label: t("knowledgeBase.showEnlarged") as string,
                icon: "pi pi-window-maximize",
                command: () => openEnlarged(contextAsset),
            });
        }
        if (canManage) {
            items.push({
                label: t("common.action.delete") as string,
                icon: "pi pi-trash",
                command: () => handleDeleteAttachment(contextAsset.uuid),
            });
        }
        return items;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contextAsset, canManage, t]);

    // Everything the page shows is asked for at once; nothing waits for anything else.
    useEffect(() => {
        if (!uuid) {
            return;
        }
        setMediaLoading(true);
        load();
        loadMedia();
        loadReviewSummary();
        knowledgeArticleService.registerView(uuid).catch(() => undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uuid]);

    const togglePin = async () => {
        if (!uuid) {
            return;
        }
        try {
            const response = await knowledgeArticleService.togglePin(uuid);
            setPinned(response.data.data === true);
        } catch (error) {
            toast.error(resolveApiErrorMessage(error, "knowledgeBase.saveError", t));
        }
    };

    const handleStatusChange = async (nextStatus: string) => {
        if (!uuid || !article || statusSaving) {
            return;
        }
        setStatusSaving(true);
        try {
            await knowledgeArticleService.putItem(uuid, { ...toKnowledgeArticleCrudModel(article), status: nextStatus });
            setArticle({ ...article, status: nextStatus });
            toast.success(t("common.saved") as string);
            refreshActivityIfNeeded();
        } catch (error) {
            toast.error(resolveApiErrorMessage(error, "knowledgeBase.saveError", t));
        } finally {
            setStatusSaving(false);
        }
    };

    const handleExportPdf = async () => {
        if (!article || exportingPdf || exportingFull) {
            return;
        }
        setExportingPdf(true);
        try {
            await exportKnowledgeArticleToPdf({
                article,
                categories,
                sections,
                mediaAssets,
                mediaAssetService,
                t,
            });
            toast.success(t("knowledgeBase.exportPdfSuccess") as string);
        } catch (error) {
            toast.error(resolveApiErrorMessage(error, "knowledgeBase.exportPdfError", t));
        } finally {
            setExportingPdf(false);
        }
    };

    const handleExportFull = async () => {
        if (!article || !uuid || exportingPdf || exportingFull) {
            return;
        }
        setExportingFull(true);
        try {
            const timeline =
                canManage && (!activityLoaded || activityStale) ? await loadEvents() : events;
            await exportKnowledgeArticleFullArchive({
                articleUuid: uuid,
                article,
                categories,
                sections,
                mediaAssets,
                events: timeline,
                mediaAssetService,
                t,
            });
            toast.success(t("knowledgeBase.exportFullSuccess") as string);
        } catch (error) {
            toast.error(resolveApiErrorMessage(error, "knowledgeBase.exportFullError", t));
        } finally {
            setExportingFull(false);
        }
    };

    useEffect(() => {
        const headerActions = uuid
            ? [
                ...(canManage
                    ? [{
                        key: "edit",
                        label: t("knowledgeBase.editArticle") as string,
                        icon: "pi pi-pencil",
                        command: () => navigate(knowledgeArticleEditPath(uuid)),
                    }]
                    : []),
                ...(canManage && article?.status === KNOWLEDGE_ARTICLE_STATUS.DRAFT
                    ? [{
                        key: "publish",
                        label: t("knowledgeBase.detail.publish") as string,
                        icon: statusSaving ? "pi pi-spinner pi-spin" : "pi pi-send",
                        command: () => { void handleStatusChange(KNOWLEDGE_ARTICLE_STATUS.PUBLISHED); },
                        disabled: statusSaving,
                    }]
                    : []),
                ...(canManage && article?.status === KNOWLEDGE_ARTICLE_STATUS.PUBLISHED
                    ? [{
                        key: "draft",
                        label: t("knowledgeBase.detail.setDraft") as string,
                        icon: statusSaving ? "pi pi-spinner pi-spin" : "pi pi-file",
                        command: () => { void handleStatusChange(KNOWLEDGE_ARTICLE_STATUS.DRAFT); },
                        disabled: statusSaving,
                    }]
                    : []),
                {
                    key: "pdf",
                    label: t("knowledgeBase.exportPdf") as string,
                    icon: exportingPdf ? "pi pi-spinner pi-spin" : "pi pi-file-pdf",
                    command: () => { void handleExportPdf(); },
                    disabled: exportingPdf || exportingFull || !article,
                },
                {
                    key: "full",
                    label: t("knowledgeBase.exportFull") as string,
                    icon: exportingFull ? "pi pi-spinner pi-spin" : "pi pi-download",
                    command: () => { void handleExportFull(); },
                    disabled: exportingPdf || exportingFull || !article,
                },
                {
                    key: "pin",
                    label: (pinned ? t("knowledgeBase.pinned") : t("knowledgeBase.togglePin")) as string,
                    icon: pinned ? "pi pi-bookmark-fill" : "pi pi-bookmark",
                    command: () => { void togglePin(); },
                    active: pinned,
                },
            ]
            : [];

        setPageHeader({
            title: {
                value: article?.title ?? "",
                translationKey: false,
                icon: resolveKnowledgeArticleIconClass(article?.icon),
                iconFramed: true,
            },
            actions: uuid ? (
                <>
                    <LinkedKnowledgeArticlesInfoButton
                        location={RESOURCE_LOCATION.KNOWLEDGE_ARTICLE}
                        locationIdentifier={uuid}
                    />
                    <div className="knowledge-article-detail__header-actions knowledge-article-detail__header-actions--wide">
                        {headerActions.map((action) => (
                            <button
                                key={action.key}
                                type="button"
                                className={`knowledge-article-detail__pin${action.active ? " knowledge-article-detail__pin--active" : ""}`}
                                onClick={action.command}
                                disabled={action.disabled}
                            >
                                <i className={action.icon} />
                                {action.label}
                            </button>
                        ))}
                    </div>
                    <div className="knowledge-article-detail__header-actions knowledge-article-detail__header-actions--compact">
                        <Menu
                            model={headerActions.map(({ label, icon, command, disabled }) => ({
                                label,
                                icon,
                                command,
                                disabled,
                            }))}
                            popup
                            ref={articleHeaderMenuRef}
                            popupAlignment="right"
                        />
                        <button
                            type="button"
                            className="knowledge-article-detail__pin"
                            aria-label={t("common.action.more", "Meer") as string}
                            aria-haspopup="menu"
                            onClick={(event) => articleHeaderMenuRef.current?.toggle(event)}
                        >
                            <i className="pi pi-ellipsis-v" />
                            <span className="knowledge-article-detail__pin-label">{t("common.actions")}</span>
                        </button>
                    </div>
                </>
            ) : undefined,
        });
        return () => resetPageHeader();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [article, categories, sections, mediaAssets, events, canManage, pinned, uuid, exportingPdf, exportingFull, statusSaving]);

    const activityEvents = useMemo(
        () => events.map((event) => ({ ...event, eventType: event.eventType || "message" })),
        [events]
    );

    const resolveActivityEventLabel = (event: IDetailedActivityEvent): string =>
        event.eventType.startsWith(KNOWLEDGE_ARTICLE_EVENT_PREFIX)
            ? (t(`knowledgeBase.event.${event.eventType}`) as string)
            : resolveDetailedActivityEventLabel(event.eventType, t);

    const readingTimeMinutes = useMemo(
        () =>
            estimateReadingTimeMinutes(
                sections
                    .filter((section) => section.sectionType === SECTION_TYPE.TEXT)
                    .map((section) => section.content ?? "")
                    .join(" ")
            ),
        [sections]
    );

    const ownRatingValue = reviewSummary?.myRatingValue ?? null;
    const hasOwnRating = ownRatingValue !== null;
    // Held back until the summary is in, so the question does not flash by for someone who already answered.
    const showReviewWidget = reviewSummaryLoaded && (!hasOwnRating || reviewExpanded);
    const ownRatingIcon = ownRatingValue === 0 ? "pi pi-thumbs-down-fill" : "pi pi-thumbs-up-fill";

    const helpfulPercentage = useMemo(
        () => (reviewSummary && reviewSummary.count > 0 ? Math.round((reviewSummary.average ?? 0) * 100) : null),
        [reviewSummary]
    );

    const articleTags = useMemo(
        () =>
            (article?.tags ?? "")
                .split(",")
                .map((tag) => tag.trim())
                .filter(Boolean),
        [article?.tags]
    );

    // Plain FILE uploads plus every section's media — IMAGE/VIDEO/SLIDESHOW items and TEXT-section inline
    // photos. Readers expect to find/download/enlarge them from a single Attachments list.
    const attachmentAssets = mediaAssets;
    // A manager always gets the card: it holds the upload button, also for the very first attachment.
    const showAttachments = attachmentAssets.length > 0 || canManage;

    const handleMediaChanged = async () => {
        await loadMedia();
        refreshActivityIfNeeded();
    };

    if (loadFailed) {
        return (
            <div className="knowledge-article-detail">
                <PageBackLink to={KNOWLEDGE_BASE_LIST_PATH} label={t("knowledgeBase.backToList") as string} />
                <div className="knowledge-article-detail__card">
                    <p>{t("knowledgeBase.notAvailable")}</p>
                </div>
            </div>
        );
    }

    if (!article || !uuid) {
        return (
            <div className="knowledge-article-detail knowledge-article-detail--loading">
                <i className="pi pi-spinner pi-spin" />
                <span>{t("common.loading")}</span>
            </div>
        );
    }

    return (
        <div className="knowledge-article-detail">
            <PageBackLink to={KNOWLEDGE_BASE_LIST_PATH} label={t("knowledgeBase.backToList") as string} />

            <div className="knowledge-article-detail__layout">
                <div className="knowledge-article-detail__primary">
                    <div className="knowledge-article-detail__main">
                        {sections.map((section) => (
                            <ArticleSectionViewer
                                key={section.uuid}
                                section={section}
                                highlighted={highlightedSectionUuid === section.uuid}
                                mediaAssets={mediaAssets}
                                mediaLoading={mediaLoading}
                            />
                        ))}
                    </div>
                    <div className="knowledge-article-detail__comments">
                        <div className="knowledge-article-detail__card">
                            <div className="knowledge-article-detail__card-header">
                                <span className="knowledge-article-detail__card-title">
                                    <i className="pi pi-comments" />
                                    {t("knowledgeBase.comments")}
                                </span>
                                <span className="knowledge-article-detail__card-peek">{commentCount}</span>
                            </div>
                            <DashlyCommentSection
                                location={RESOURCE_LOCATION.KNOWLEDGE_ARTICLE}
                                locationIdentifier={uuid}
                                canEdit={canComment}
                                canModerate={canManage}
                                currentUserUuid={currentUserUuid}
                                showHeader={false}
                                onCommentsChanged={refreshActivityIfNeeded}
                                onCommentsLoaded={setCommentCount}
                            />
                        </div>
                    </div>
                </div>

                <div className="knowledge-article-detail__rail">
                    <div className="knowledge-article-detail__ask">
                        <KnowledgeBaseAskAi articleUuid={uuid} variant="detail" />
                    </div>
                    {/* Always there: the statistics and the rating are for every reader. */}
                    <div className="knowledge-article-detail__side">
                    {(isFeaturedActive(article) || (canManage && isFeaturedExpired(article))) && (
                        <div
                            className={`knowledge-article-detail__card knowledge-article-detail__card--featured${
                                isFeaturedExpired(article) ? " knowledge-article-detail__card--featured-expired" : ""
                            }`}
                        >
                            <div className="knowledge-article-detail__card-header">
                                <span className="knowledge-article-detail__card-title">
                                    <i className={isFeaturedActive(article) ? "pi pi-star-fill" : "pi pi-star"} />
                                    {t("knowledgeBase.featuredSection")}
                                </span>
                            </div>
                            <p className="knowledge-article-detail__featured-status">
                                {isFeaturedExpired(article)
                                    ? t("knowledgeBase.featuredExpired")
                                    : t("knowledgeBase.detail.featuredActive")}
                            </p>
                            <p className="knowledge-article-detail__featured-until">
                                {article.featuredExpiresAt
                                    ? t("knowledgeBase.detail.featuredUntil", {
                                          date: formatDisplayDate(article.featuredExpiresAt),
                                      })
                                    : t("knowledgeBase.detail.featuredNoEnd")}
                            </p>
                        </div>
                    )}
                    <div className="knowledge-article-detail__card">
                        <div className="knowledge-article-detail__card-header">
                            <span className="knowledge-article-detail__card-title">
                                <i className="pi pi-chart-bar" />
                                {t("knowledgeBase.statistics")}
                            </span>
                        </div>
                        <div className="knowledge-article-detail__stats">
                            <div className="knowledge-article-detail__stat">
                                <i className="pi pi-clock" />
                                <span className="knowledge-article-detail__stat-value">{readingTimeMinutes}</span>
                                <span className="knowledge-article-detail__stat-label">{t("knowledgeBase.statReadingTime")}</span>
                            </div>
                            <div className="knowledge-article-detail__stat">
                                {hasOwnRating ? (
                                    <button
                                        type="button"
                                        className="knowledge-article-detail__stat-rating"
                                        aria-pressed
                                        aria-label={t("review.withdraw") as string}
                                        title={t("review.withdraw") as string}
                                        disabled={withdrawingReview}
                                        onClick={withdrawOwnReview}
                                    >
                                        <i className={ownRatingIcon} />
                                    </button>
                                ) : (
                                    <i className="pi pi-thumbs-up" />
                                )}
                                <span className="knowledge-article-detail__stat-value">
                                    {helpfulPercentage !== null ? `${helpfulPercentage}%` : "—"}
                                </span>
                                <span className="knowledge-article-detail__stat-label">{t("knowledgeBase.statHelpful")}</span>
                            </div>
                            <div className="knowledge-article-detail__stat">
                                <i className="pi pi-eye" />
                                <span className="knowledge-article-detail__stat-value">{article.viewCount ?? 0}</span>
                                <span className="knowledge-article-detail__stat-label">{t("knowledgeBase.statViews")}</span>
                            </div>
                        </div>
                    </div>

                    {showReviewWidget && (
                        <div className="knowledge-article-detail__card knowledge-article-detail__card--review">
                            <ReviewWidget
                                location={RESOURCE_LOCATION.KNOWLEDGE_ARTICLE}
                                locationIdentifier={uuid}
                                ratingScale="THUMBS"
                                ownRatingValue={reviewSummary?.myRatingValue}
                                ownFeedbackText={reviewSummary?.myFeedbackText}
                                onChanged={() => {
                                    // A thumbs-down still asks for feedback: keep the card while the rating
                                    // it just saved comes back with the summary.
                                    setReviewExpanded(true);
                                    void loadReviewSummary();
                                }}
                                onCompleted={() => setReviewExpanded(false)}
                            />
                        </div>
                    )}

                    {categories.length > 0 && (
                        <div className="knowledge-article-detail__card">
                            <div className="knowledge-article-detail__card-header">
                                <span className="knowledge-article-detail__card-title">
                                    <i className="pi pi-tag" />
                                    {t("knowledgeBase.form.categories")}
                                </span>
                            </div>
                            <div className="knowledge-article-detail__chip-list">
                                {categories.map((category) => (
                                    <span className="knowledge-article-detail__badge" key={category.categoryUuid}>
                                        {category.categoryName}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {(articleTags.length > 0 ||
                        article.global ||
                        (canManage && article.status !== KNOWLEDGE_ARTICLE_STATUS.PUBLISHED)) && (
                        <div className="knowledge-article-detail__card">
                            <div className="knowledge-article-detail__card-header">
                                <span className="knowledge-article-detail__card-title">
                                    <i className="pi pi-hashtag" />
                                    {t("knowledgeBase.form.tags")}
                                </span>
                            </div>
                            <div className="knowledge-article-detail__chip-list">
                                {article.global && (
                                    <span className="knowledge-article-detail__badge knowledge-article-detail__badge--global">
                                        <i className="pi pi-share-alt" />
                                        {t(
                                            article.manageable === false
                                                ? "knowledgeBase.badge.sharedFromAbove"
                                                : "knowledgeBase.badge.global"
                                        )}
                                    </span>
                                )}
                                {canManage && article.status !== KNOWLEDGE_ARTICLE_STATUS.PUBLISHED && (
                                    <span
                                        className={`knowledge-article-detail__badge knowledge-article-detail__badge--status-${article.status.toLowerCase()}`}
                                    >
                                        {t(`knowledgeBase.status.${article.status.toLowerCase()}`)}
                                    </span>
                                )}
                                {articleTags.map((tag) => (
                                    <span className="knowledge-article-detail__badge" key={tag}>
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {showAttachments && (
                        <div className="knowledge-article-detail__card">
                            <div className="knowledge-article-detail__card-header">
                                <span className="knowledge-article-detail__card-title">
                                    <i className="pi pi-download" />
                                    {t("knowledgeBase.attachments")}
                                </span>
                                {canManage && (
                                    <div className="knowledge-article-detail__card-header-actions">
                                        <button
                                            type="button"
                                            className="knowledge-article-detail__manage-media-toggle"
                                            title={t("knowledgeBase.form.uploadMedia") as string}
                                            onClick={(e) => mediaManagerRef.current?.openUploadMenu(e)}
                                        >
                                            <i className="pi pi-upload" />
                                        </button>
                                    </div>
                                )}
                            </div>
                            {attachmentAssets.length > 0 && (
                                <ul className="knowledge-article-detail__file-list">
                                    {attachmentAssets.map((asset) => (
                                        <li
                                            key={asset.uuid}
                                            onContextMenu={(e) => {
                                                e.preventDefault();
                                                setContextAsset(asset);
                                                attachmentContextMenuRef.current?.show(e);
                                            }}
                                        >
                                            <button
                                                type="button"
                                                className="knowledge-article-detail__file-link"
                                                title={asset.originalFilename}
                                                onClick={() => (isEnlargeable(asset) ? openEnlarged(asset) : handleFileDownload(asset.uuid))}
                                            >
                                                {mediaThumbnailUrl(asset) ? (
                                                    <img
                                                        className="knowledge-article-detail__file-thumb"
                                                        src={mediaThumbnailUrl(asset)}
                                                        alt=""
                                                    />
                                                ) : (
                                                    <i className={`pi ${isEnlargeableVideo(asset) ? "pi-video" : "pi-file"}`} />
                                                )}
                                                <span className="knowledge-article-detail__file-name">{asset.originalFilename}</span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {canManage && (
                                // Delete/download/enlarge are already on the list above via right-click —
                                // this instance only backs the upload icon's hidden file input.
                                <MediaManager
                                    ref={mediaManagerRef}
                                    knowledgeArticleUuid={uuid}
                                    onChanged={handleMediaChanged}
                                    hideUploadButton
                                    showList={false}
                                />
                            )}
                        </div>
                    )}
                    </div>
                    {canManage && (
                        <div className="knowledge-article-detail__activity">
                            <div
                                className={`knowledge-article-detail__card${
                                    activityOpen ? "" : " knowledge-article-detail__card--collapsed"
                                }`}
                            >
                                <button
                                    type="button"
                                    className="knowledge-article-detail__card-header knowledge-article-detail__card-header--toggle"
                                    aria-expanded={activityOpen}
                                    onClick={toggleActivity}
                                >
                                    <span className="knowledge-article-detail__card-title">
                                        <i className="pi pi-history" />
                                        {t("knowledgeBase.activity")}
                                    </span>
                                    <span className="knowledge-article-detail__card-header-end">
                                        {activityLoaded && (
                                            <span className="knowledge-article-detail__card-peek">{activityEvents.length}</span>
                                        )}
                                        <i
                                            className={`pi ${activityOpen ? "pi-chevron-up" : "pi-chevron-down"}`}
                                            aria-hidden
                                        />
                                    </span>
                                </button>
                                {activityOpen && (
                                    activityLoading && !activityLoaded ? (
                                        <div className="knowledge-article-detail__activity-loading">
                                            <i className="pi pi-spinner pi-spin" />
                                            <span>{t("common.loading")}</span>
                                        </div>
                                    ) : (
                                        <DetailedActivityStream
                                            events={activityEvents}
                                            eventLabelResolver={resolveActivityEventLabel}
                                            emptyMessage={t("knowledgeBase.activityEmpty") as string}
                                            maxHeight="360px"
                                            showHeader={false}
                                        />
                                    )
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <ContextMenu ref={attachmentContextMenuRef} model={attachmentMenuItems} />

            <AppDialog
                visible={!!enlargedAsset}
                onHide={() => setEnlargedAsset(null)}
                header={enlargedAsset?.asset.originalFilename}
                style={{ width: "90vw", height: "90vh" }}
                contentStyle={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", background: "#0f172a", overflow: "hidden" }}
            >
                {enlargedAsset?.videoSources ? (
                    <VideoPlayer sources={enlargedAsset.videoSources} poster={enlargedAsset.asset.thumbnailUrl} style={{ width: "100%", height: "100%" }} />
                ) : (
                    enlargedAsset && (
                        <img
                            src={photoDisplayUrl(enlargedAsset.asset)}
                            alt={enlargedAsset.asset.originalFilename}
                            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                        />
                    )
                )}
            </AppDialog>
        </div>
    );
};

/** One page instance per article: going from one article to another (a relation, an Ask AI link) stays on
 *  this route, and must not show the previous article's content while the next one loads. */
const KnowledgeArticleDetail: React.FC = () => {
    const { uuid } = useParams<{ uuid: string }>();
    return <KnowledgeArticleDetailPage key={uuid} />;
};

export default KnowledgeArticleDetail;
